import React, { useEffect, useState } from "react";
import {
  AspectRatio,
  Box,
  Button,
  Center,
  HStack,
  Icon,
  IconButton,
  Image,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { FiDownload, FiFile, FiImage } from "react-icons/fi";
import { downloadFileApi, getFileBlobApi } from "../../api/files";

export const formatFileSize = (size = 0) =>
  size < 1024 * 1024 ? `${Math.max(1, Math.round(size / 1024))} KB` : `${(size / 1024 / 1024).toFixed(1)} MB`;

const FileList = ({ files = [], compact = false }) => {
  const toast = useToast();
  if (!files.length) return null;
  const download = async (file) => {
    try { await downloadFileApi(file); }
    catch (error) { toast({ status: "error", title: error.response?.data?.msg || "Unable to download file" }); }
  };
  const images = files.filter((file) => file.mimeType?.startsWith("image/"));
  const documents = files.filter((file) => !file.mimeType?.startsWith("image/"));
  return (
    <Box mt={compact ? "2" : "3"}>
      {!!images.length && (
        <SimpleGrid columns={compact ? 1 : { base: 1, sm: 2, lg: 3 }} spacing="2" mb={documents.length ? "3" : "0"}>
          {images.map((file) => (
            <ProtectedImage key={file._id} file={file} compact={compact} onDownload={() => download(file)} />
          ))}
        </SimpleGrid>
      )}
      <VStack align="stretch" spacing="2">
      {documents.map((file) => (
        <Button key={file._id} variant="outline" h="auto" py="2" px="3" justifyContent="space-between" onClick={() => download(file)}>
          <HStack minW="0" textAlign="left">
            <Icon as={file.mimeType?.startsWith("image/") ? FiImage : FiFile} flexShrink="0" />
            <Text fontSize="sm" noOfLines={1}>{file.originalName}</Text>
            <Text fontSize="xs" color="gray.500" flexShrink="0">{formatFileSize(file.size)}</Text>
          </HStack>
          <Icon as={FiDownload} ml="2" flexShrink="0" />
        </Button>
      ))}
      </VStack>
    </Box>
  );
};

const ProtectedImage = ({ file, compact, onDownload }) => {
  const [source, setSource] = useState("");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    let objectUrl;
    getFileBlobApi(file._id)
      .then((response) => {
        objectUrl = window.URL.createObjectURL(response.data);
        if (active) setSource(objectUrl);
      })
      .catch(() => active && setFailed(true));
    return () => {
      active = false;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [file._id]);

  return (
    <Box position="relative" overflow="hidden" borderRadius="xl" borderWidth="1px" bg="gray.50" maxW={compact ? "260px" : undefined}>
      <AspectRatio ratio={compact ? 4 / 3 : 16 / 10}>
        {!source && !failed ? (
          <Center><Spinner size="sm" color="brand.500" /></Center>
        ) : failed ? (
          <Center flexDirection="column" color="gray.500"><Icon as={FiImage} /><Text mt="1" fontSize="xs">Preview unavailable</Text></Center>
        ) : (
          <Image src={source} alt={file.originalName} objectFit="cover" />
        )}
      </AspectRatio>
      <HStack position="absolute" bottom="0" left="0" right="0" px="2" py="1.5" bg="blackAlpha.700" color="white" justify="space-between">
        <Box minW="0"><Text fontSize="xs" fontWeight="700" noOfLines={1}>{file.originalName}</Text><Text fontSize="10px" opacity="0.8">{formatFileSize(file.size)}</Text></Box>
        <IconButton size="xs" variant="ghost" color="white" _hover={{ bg: "whiteAlpha.300" }} icon={<FiDownload />} aria-label={`Download ${file.originalName}`} onClick={onDownload} />
      </HStack>
    </Box>
  );
};
export default FileList;
